import { inject, Pipe, PipeTransform, Signal } from '@angular/core';
import { IrdFormattable } from '@ird/shared-base';
import { IrdFormatService } from '../services/format/ird-format.service';

/**
 * `value | irdFormat: property` — the template side of `IrdFormatService`, which it only delegates
 * to. The cell, its tooltip and the detail view read the same string.
 *
 * Pure, so it memoises on argument identity. That holds because every resolved property is
 * created once, by the fold — never build the property argument as an inline object literal.
 * A pure pipe does not see a language switch or translations that arrive late, so a template that
 * stays on screen across them passes a value that changes with them as the last argument; it is
 * not read, it only re-runs the pipe.
 */
@Pipe({ name: 'irdFormat' })
export class IrdFormatPipe implements PipeTransform {
  private readonly formatService = inject(IrdFormatService);

  public transform(value: unknown, property: IrdFormattable, _translations?: unknown): string {
    return this.formatService.format(value, property);
  }
}

/**
 * Counts language switches and translations that arrive late — which `setTranslation` reports only
 * as a translation change. Pass it to `irdFormat` as the last argument, or read it in a `computed`
 * that resolves labels in TypeScript. Call it in an injection context.
 */
export function translationsVersion(): Signal<number> {
  return inject(IrdFormatService).translationsVersion;
}
